import {pickKeysLike} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {Ref, useMemo} from 'react';
import {COLORS} from '../../../constants/colors';
import {desktopAndUpLinaria} from '../../../constants/customMedia';
import {BORDER, RADIUS, SPACING} from '../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {AriaButtonLinkType} from '../../../types/AriaButtonLink';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';
import {UnstyledLinkLinaria} from '../UnstyledLink/UnstyledLinkLinaria';
import {ButtonLoadingIcon} from './ButtonLoadingIcon';
import {UnstyledButton} from './UnstyledButton';
import {ButtonProps, StyledButtonProps} from './types';

export const Button = ({
  href,
  onClick,
  disabled,
  'data-testid': testId,
  openExternalLinkInNewTab,
  openLinkInNewTab,
  'aria-label': ariaLabel,
  variant = 'primary',
  htmlType,
  size = 'full-width',
  round = false,
  children,
  disableTabNavigation,
  rightIcon,
  leftIcon,
  isLoading,
  loadingIconComponent,
  onClose,
  count,
  ref,
  ...otherProps
}: ButtonProps & AriaButtonLinkType & {ref?: Ref<HTMLElement>}) => {
  const dataAttrProps = pickKeysLike(otherProps, 'data-');

  const isRounded = round || variant === 'floating';
  const isButtonLoading = isLoading && !disabled;

  const sharedProps = {
    onClick,
    disabled,
    'data-testid': testId,
    'aria-label': ariaLabel,
    'data-variant': variant,
    'data-size': size,
    'data-round': isRounded,
    'data-with-close-icon': Boolean(onClose),
    'data-is-loading': isButtonLoading,
    tabIndex: disableTabNavigation ? -1 : undefined,
  };

  const content = useMemo(
    () => (
      <>
        <ContentWrapper
          style={{visibility: isButtonLoading ? 'hidden' : 'visible'}}
        >
          {leftIcon}
          {children}
          {rightIcon}
        </ContentWrapper>
        {isButtonLoading &&
          (loadingIconComponent ?? (
            <ButtonLoadingIcon
              variant={
                variant === 'secondary' || (variant === 'floating' && !onClose)
                  ? 'dark'
                  : 'light'
              }
            />
          ))}
      </>
    ),
    [
      children,
      isButtonLoading,
      leftIcon,
      loadingIconComponent,
      rightIcon,
      variant,
      onClose,
    ],
  );

  const component =
    href && !disabled ? (
      <LinkButton
        {...sharedProps}
        {...otherProps}
        {...dataAttrProps}
        ref={ref as Ref<HTMLAnchorElement>}
        // link props
        to={href}
        openExternalLinkInNewTab={openExternalLinkInNewTab}
        openLinkInNewTab={openLinkInNewTab}
      >
        {content}
      </LinkButton>
    ) : (
      <StyledButton
        {...sharedProps}
        {...otherProps}
        {...dataAttrProps}
        ref={ref as Ref<HTMLButtonElement>}
        type={htmlType}
      >
        {content}
      </StyledButton>
    );

  if (onClose) {
    return (
      <CloseIconWrapper className={otherProps.className}>
        {count !== undefined && count > 0 && (
          <CountWrapper>{count}</CountWrapper>
        )}
        {component}
        <CloseButton disabled={disabled} onClick={onClose} aria-label="Close">
          <Icon
            asset={GenericIconName.Close}
            size="SS"
            fill={
              disabled ? COLORS['Text.Grey.Inactive'] : COLORS['Text.Black']
            }
          />
        </CloseButton>
      </CloseIconWrapper>
    );
  }

  return component;
};

const CountWrapper = styled.span`
  display: flex;
  padding: ${SPACING['Y.Xs']} ${SPACING['X.Sm']};
  justify-content: center;
  align-items: center;
  border-radius: ${RADIUS.Round};
  background: ${COLORS['Alerts.Error.Primary']};
  color: ${COLORS['Text.White']};
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.24);
`;

const ContentWrapper = styled.span`
  display: flex;
  gap: ${SPACING['X.Md']};
  align-items: center;
  justify-content: center;
`;

const CloseIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  background-color: ${COLORS.White};
  border-radius: 100px;
  box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.2);
  padding-right: 14px;
  position: relative;
  width: fit-content;
`;

const CloseButton = styled(UnstyledButton)`
  &[disabled] {
    cursor: auto;
    pointer-events: none;
  }
`;

const buttonStyles = `
  --color: ${COLORS['Text.White']};
  --border-color: transparent;
  --border-width: ${BORDER.Sm};
  --background-color: ${COLORS['Fill.Black']};
  --default-transition: background-color 300ms ease-in-out, box-shadow 300ms ease-in-out;

  align-items: center;
  background-color: var(--background-color);
  border-radius: ${RADIUS.Sm};
  border: 0;
  box-shadow: inset 0 0 0 var(--border-width) var(--border-color);
  color: var(--color);
  cursor: pointer;
  display: flex;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  min-height: 32px;
  justify-content: center;
  line-height: 1.5;
  margin: 0;
  max-width: 96px;
  opacity: 1;
  outline: none;
  padding-inline: ${SPACING['X.Xl']};
  position: relative;
  text-align: center;
  text-decoration: none;
  transition: var(--default-transition);
  width: 100%;

  &&&:focus-visible {
    outline: none;
  }
  
  &[data-size="sm"] {
    min-height: 48px;
    max-width: 48px;
    padding-inline: 0;
    width: 48px;
  }

  &[data-size="lg"] {
    min-height: 48px;
    max-width: 343px;
    width: 100%;
  }
  
  &[data-size="full-width"] {
    min-height: 48px;
    max-width: 100%;
    width: 100%;
  }
  
  &[data-size="fit-width"] {
    min-height: 48px;
    max-width: 100%;
    width: fit-content;
  }
  
  &[data-variant="primary"] {
    &:active {
      --background-color: ${COLORS['Button.Primary.Hover']};
    }

    ${desktopAndUpLinaria(`
      &:hover {
        --background-color: ${COLORS['Button.Primary.Hover']};
      }
    `)}

    &:focus-visible {
      --border-color: ${COLORS['Text.Grey.Inactive']};
    }

    &[disabled] {
      --background-color: ${COLORS['Fill.Grey.Primary']};
      --color: ${COLORS['Text.Grey.Inactive']};
    }
  }

  &[data-variant="secondary"] {
    --color: ${COLORS['Text.Black']};
    --background-color: ${COLORS['Fill.White']};
    --border-color: ${COLORS['Outline.Grey']};
  }

  &[data-variant="glassy"] {
    --color: ${COLORS['Text.White']};
    --background-color: rgba(255, 255, 255, 0.24);
    --border-color: ${COLORS['Fill.White']};
    --border-width: ${BORDER.Sm};

    -webkit-backdrop-filter: blur(16px);
    backdrop-filter: blur(16px);

    &[disabled] {
      --color: rgba(255, 255, 255, 0.40);
      opacity: 0.4;
    }
  }
    
  &[data-variant="floating"] {
    --color: ${COLORS['Text.Black']};
    --background-color: ${COLORS['Fill.White']};
    --border-color: ${COLORS['Outline.Grey']};
    --border-width: ${BORDER.Sm};
    
    box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.24);
    min-height: 48px;
    max-width: 48px;

    &[data-with-close-icon="true"] {
      --color: ${COLORS['Text.White']};
      --background-color: ${COLORS['Fill.Black']};
      --border-width: 0;

      box-shadow: unset;

      &:focus-visible {
        --border-width: ${BORDER.Lg};
      }
      
      &[disabled] {        
        --background-color: ${COLORS['Fill.Grey.Primary']};
      }
    }
    
    &:focus-visible {
      --border-color: ${COLORS['Text.Grey.Inactive']};
    }

    &:not([data-size="sm"]) {
      max-width: 96px;
    }

    &[data-size="fit-width"] {
      max-width: fit-content;
    }
  }

  &[disabled] {
    --color: ${COLORS['Text.Grey.Inactive']};
    
    cursor: auto;
    pointer-events: none;
  }
  
  &[data-is-loading="true"] {
    cursor: auto;
    pointer-events: none;
  }

  &[data-round="true"] {
    border-radius: ${RADIUS.Round};
  }

  &:visited,
  &:hover {
    color: var(--color);
  }

  &&:hover,
  &&:active,
  &&:focus {
    --border-width: ${BORDER.Md};
  }

  &&:focus-visible {
    --border-width: ${BORDER.Lg};
  }
`;

const StyledButton = styled.button<StyledButtonProps>`
  ${buttonStyles}
`;

const LinkButton = styled(UnstyledLinkLinaria)<StyledButtonProps>`
  ${buttonStyles}
`;
